import { useEffect, useRef, useState } from 'react';
import { apiGet } from '@/lib/api/client';

export interface PlaceSuggestion {
  id: string;
  placeId: string;
  primaryText: string;
  secondaryText: string;
  fullText: string;
}

/**
 * Debounced Google Places autocomplete. Returns predictions for the given query
 * (min 3 chars), aborting in-flight requests as the query changes. A stable
 * per-mount session token keeps the autocomplete billing/session consistent.
 */
export function usePlacesAutocomplete(
  query: string,
  options: { enabled?: boolean } = {},
): { suggestions: PlaceSuggestion[]; isLoading: boolean } {
  const { enabled = true } = options;
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionTokenRef = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  );

  useEffect(() => {
    const q = query.trim();
    // `enabled: false` lets the caller suppress the fetch (e.g. right after a
    // selection set the input to the chosen place — no need to re-query it).
    if (!enabled || q.length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ input: q, sessionToken: sessionTokenRef.current });
        const { data } = await apiGet<{ predictions: PlaceSuggestion[] }>(
          `/places/autocomplete?${params.toString()}`,
          { signal: controller.signal },
        );
        setSuggestions(data.predictions ?? []);
      } catch (error: any) {
        if (error?.name !== 'AbortError') setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, enabled]);

  return { suggestions, isLoading };
}
